<script>
import {
  iFeaturedIcon,
  iFeaturedIconSizes,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
} from '@inscribe/signal';
import RiskBadge from '@/V2/components/status/RiskBadge.vue';
import CustomerReviewStatusBadge from '@/V2/components/status/CustomerReviewStatusBadge.vue';
import { RISK_LEVEL } from '@/constants/riskLevels';
import { getWebBaseUrl } from '@/environment';

export default {
  name: 'CustomerSearchResult',
  components: {
    iFeaturedIcon,
    iButton,
    RiskBadge,
    CustomerReviewStatusBadge,
    iKeyIcon,
  },
  props: {
    customer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      iFeaturedIconSizes,
      iButtonSizes,
      iButtonStyles,
      iKeyIconShape,
      iKeyIconStyle,
      iKeyIconType,
    };
  },
  computed: {
    riskLevel() {
      if (this.customer?.risk_level) {
        return RISK_LEVEL[this.customer.risk_level];
      }

      const agentRiskLevel =
        this.customer?.customer_review_agent_v1?.output?.risk_level;
      if (agentRiskLevel) {
        return RISK_LEVEL[agentRiskLevel];
      }

      const fraudRating = this.customer?.fraud_rating;
      if (fraudRating === 'HIGH_RISK') {
        return RISK_LEVEL.HIGH;
      } else if (fraudRating === 'UNCERTAIN_RISK') {
        return RISK_LEVEL.MEDIUM;
      } else if (fraudRating === 'LOW_RISK') {
        return RISK_LEVEL.LOW;
      }

      return null;
    },
    formattedDate() {
      if (!this.customer.created_at) return '';
      const date = new Date(this.customer.created_at);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    shouldShowCustomerReviewStatusBadge() {
      return typeof this.customer?.approved === 'boolean';
    },
  },
  methods: {
    onResultClicked() {
      this.$emit('click');
    },
    onOpenInNewTabClicked() {
      window.open(
        `${getWebBaseUrl()}/#/customer/${this.customer.id}`,
        '_blank',
      );
    },
  },
};
</script>

<template>
  <div class="result-item d-flex align-items-center i-gap-6">
    <router-link
      :to="{
        name: 'customer-overview',
        params: { customer_id: customer.id },
      }"
      class="result-link"
      data-dd-action-name="global-search-result-customer"
      @click="onResultClicked"
    >
      <i-key-icon
        class="avatar"
        icon="medium-user-single"
        :shape="iKeyIconShape.SQUARE"
        :type="iKeyIconType.NEUTRAL"
        :icon-style="iKeyIconStyle.OUTLINE"
      />

      <div class="content-wrapper">
        <div class="content">
          <div class="name text-truncate">{{ customer.name }}</div>
          <div class="meta text-truncate">
            {{ customer.no_of_documents }} Document{{
              customer.no_of_documents === 1 ? '' : 's'
            }}
            • {{ formattedDate }}
          </div>
        </div>

        <div class="badges d-flex align-items-center i-gap-2">
          <risk-badge
            v-if="riskLevel"
            :risk-level="riskLevel"
            class="flex-shrink-0"
          ></risk-badge>
          <customer-review-status-badge
            v-if="shouldShowCustomerReviewStatusBadge"
            :is-approved="customer?.approved"
            class="flex-shrink-0"
          ></customer-review-status-badge>
        </div>
      </div>

      <i-button
        type="neutral"
        :button-style="iButtonStyles.SOLID_LIGHT"
        left-icon="medium-link-square-1"
        :size="iButtonSizes.SMALL"
        class="new-tab-open-button ml-2"
        @button-click="onOpenInNewTabClicked"
      />
    </router-link>
  </div>
</template>

<style lang="scss" scoped>
.result-item {
  width: 100%;

  .badges {
    display: flex;
  }

  .new-tab-open-button {
    width: 28px;
    display: none;
  }

  &:hover {
    .badges {
      display: none;
    }

    .new-tab-open-button {
      display: block;
    }
  }
}

.result-link {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 0.5rem;
  border-radius: 0.5rem;
  transition: background-color 0.2s;
  text-decoration: none;
  color: inherit;
  width: 100%;
  min-width: 0;

  &:hover {
    background-color: $light-neutral-20;
    cursor: pointer;
  }
}

.content-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}

.content {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  overflow: hidden;

  .name {
    font-weight: 500;
    color: $light-neutral-120;
    min-width: 0;
  }

  .meta {
    color: $light-neutral-100;
    font-size: 0.875rem;
    min-width: 0;
  }
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
